import styled from "@emotion/styled";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import * as Sentry from "@sentry/browser";
import { useStoreState } from "pullstate";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { parseUnits } from "viem";
import { useWalletClient } from "wagmi";
import { getContract, waitForTransaction } from "wagmi/actions";
import { BEBOP_ROUTES, BLOCK_EXPLORER_URL_BY_NETWORK } from "../../../config/consts";
import { CommonButtonState } from "../../../enums/CommonButtonState";
import { useGetTokenDecimals } from "../../../hooks/useGetTokenDecimals";
import { ReactComponent as EtherscanIcon } from "../../../img/trade-interface/view_transaction.svg";
import { GlobalStore, IGlobalStore } from "../../../store/global/GlobalStore";
import { WRAPPED_ABI } from "../../../store/global/TokenEnum";
import { OrderStatus } from "../../../types/orderStatus";
import { WrapStatus } from "../../../types/wrapQuote";
import { getWrappedSymbolForChain } from "../../../utils/chainUtils";
import { getTokenAddressBySymbol } from "../../../utils/orderUtils";
import { isWrappingQuantityLargerThanBalance } from "../../../utils/wrapUtils";
import { Spinner } from "../../../components/Spinner/Spinner";
import { ConfirmTradeModal } from "../../../components/Modal/instance/ConfirmTrade/ConfirmTrade";
import { MODAL_SELECTORS } from "../../../components/Modal/instance/modalSelectors";

export const WrapActionButtonState = {
  ...CommonButtonState,
  ConfirmOrder: "ConfirmOrder",
  ViewTransaction: "ViewTransaction",
  EnterAmounts: "EnterAmounts",
  Confirming: "Confirming",
  Wrap: "Wrap",
  Unwrap: "Unwrap",
};

export type WrapActionButtonStateKeys = keyof typeof WrapActionButtonState;
const validateWrapEntry = (store: IGlobalStore): WrapActionButtonStateKeys => {
  if (!store.address) {
    return WrapActionButtonState.WalletNotConnected as WrapActionButtonStateKeys;
  }

  if (isWrappingQuantityLargerThanBalance(store) && !store.wrapQuote?.hash) {
    return WrapActionButtonState.InsufficientBalance as WrapActionButtonStateKeys;
  }

  if (!store.wrapQuote?.amount?.value.gt(0)) {
    return WrapActionButtonState.EnterAmounts as WrapActionButtonStateKeys;
  }

  if (store.wrapStatus === WrapStatus.REVIEW) {
    return WrapActionButtonState.ConfirmOrder as WrapActionButtonStateKeys;
  }

  if (store.wrapStatus === WrapStatus.IN_PROGRESS || store.wrapStatus === WrapStatus.COMPLETED) {
    return WrapActionButtonState.ViewTransaction as WrapActionButtonStateKeys;
  }

  if (store.wrapStatus === WrapStatus.CONFIRMING) {
    return WrapActionButtonState.Confirming as WrapActionButtonStateKeys;
  }

  return store.wrapQuote.type === "Wrapping"
    ? (WrapActionButtonState.Wrap as WrapActionButtonStateKeys)
    : (WrapActionButtonState.Unwrap as WrapActionButtonStateKeys);
};

const BUTTON_TEXT_MAP: Record<WrapActionButtonStateKeys, string> = {
  WalletNotConnected: "Connect Wallet",
  InsufficientBalance: "Insufficient Balance",
  WrongNetwork: "Switch Network",
  EnterAmounts: "Enter Amounts",
  Wrap: "Wrap",
  Unwrap: "Unwrap",
  ConfirmOrder: "Confirm Order",
  ViewTransaction: "View Transaction",
  Confirming: "Confirming",
};

const BUTTON_DISABLED_MAP: Record<WrapActionButtonStateKeys, boolean> = {
  WalletNotConnected: false,
  InsufficientBalance: true,
  WrongNetwork: true,
  EnterAmounts: true,
  Wrap: false,
  Unwrap: false,
  ConfirmOrder: false,
  ViewTransaction: false,
  Confirming: true,
};

const BUTTON_STYLES: Record<string, string> = {
  HappyState: "btn-bebop-primary text-white",
  ErrorState: "btn-grey",
  InbetweenState: "btn bebop-white-btn btn-lg btn-block button-font-size",
};

const BUTTON_STYLE_MAP: Record<WrapActionButtonStateKeys, string> = {
  WalletNotConnected: BUTTON_STYLES.HappyState,
  InsufficientBalance: BUTTON_STYLES.ErrorState,
  WrongNetwork: BUTTON_STYLES.InbetweenState,
  EnterAmounts: BUTTON_STYLES.ErrorState,
  Wrap: BUTTON_STYLES.HappyState,
  Unwrap: BUTTON_STYLES.HappyState,
  ConfirmOrder: BUTTON_STYLES.HappyState,
  ViewTransaction: BUTTON_STYLES.InbetweenState,
  Confirming: BUTTON_STYLES.ErrorState,
};

export const WrapActionButton: React.FC = () => {
  const [errorState, setErrorState] = useState<WrapActionButtonStateKeys>(
    WrapActionButtonState.EnterAmounts as WrapActionButtonStateKeys
  );
  const [buttonText, setButtonText] = useState<string>("");
  const wrapQuote = GlobalStore.useState(s => s.wrapQuote);
  const isWrapPage = useLocation().pathname === BEBOP_ROUTES.Wrap;
  const tokenMetadata = GlobalStore.useState(s => s.tokens);
  const txHash = GlobalStore.useState(s => s.wrapQuote?.hash);
  const { data } = useWalletClient();
  const state = useStoreState(GlobalStore);
  const wrappedToken = getWrappedSymbolForChain(state.network);
  const { data: walletClient } = useWalletClient();
  const getTokenDecimals = useGetTokenDecimals();

  // Runs when the whole store changes
  useEffect(() => {
    setErrorState(validateWrapEntry(state));
  }, [state]);

  // Runs when errorState changes
  useEffect(() => {
    if (!errorState) {
      // Push to Order Review
      setButtonText("Review Order");
    } else {
      setButtonText(BUTTON_TEXT_MAP[errorState]);
    }
  }, [errorState]);

  const getClassName = useCallback((): string => {
    if (errorState) {
      return BUTTON_STYLE_MAP[errorState];
    }
    return BUTTON_STYLES.HappyState;
  }, [errorState]);

  const getButtonIcon = useCallback(() => {
    if (errorState === WrapActionButtonState.ViewTransaction) {
      return <EtherscanIcon width={22} height={22} />;
    }
  }, [errorState]);

  const confirmWrapping = useCallback(async () => {
    GlobalStore.update(s => {
      s.wrapStatus = WrapStatus.CONFIRMING;
      s.modalSelector = MODAL_SELECTORS.CONFIRM_TRADE;
    });

    if (state.wrapQuote === undefined) {
      Sentry.captureException(`Attempting to wrap/unwrap with undefined wrapQuote`);
      return;
    }
    if (state.wrapQuote.amount === undefined) {
      Sentry.captureException(`Attempting to wrap/unwrap with undefined amount`);
      return;
    }

    if (data && walletClient) {
      const contract = getContract({
        address: getTokenAddressBySymbol(tokenMetadata, state.network, wrappedToken),
        abi: WRAPPED_ABI,
        walletClient,
      });
      const valueParam = parseUnits(
        state.wrapQuote.amount.value.toString() as `${number}`,
        getTokenDecimals(wrappedToken)
      );
      // deposit == Wrapping
      // withdraw == Unwrap WETH -> ETH]
      const txHash =
        state.wrapQuote.type === "Wrapping"
          ? await contract.write.deposit({ value: valueParam }).catch(() => {
              GlobalStore.update(s => {
                if (isWrapPage) {
                  s.wrapStatus = WrapStatus.REVIEW;
                } else {
                  s.orderStatus = OrderStatus.REVIEW;
                }
              });
            })
          : await contract.write.withdraw([valueParam]).catch(() => {
              GlobalStore.update(s => {
                if (isWrapPage) {
                  s.wrapStatus = WrapStatus.REVIEW;
                } else {
                  s.orderStatus = OrderStatus.REVIEW;
                }
              });
            });

      if (txHash) {
        setErrorState(WrapActionButtonState.ViewTransaction as WrapActionButtonStateKeys);
        GlobalStore.update(s => {
          if (isWrapPage) {
            s.wrapStatus = WrapStatus.IN_PROGRESS;
          } else {
            s.orderStatus = OrderStatus.IN_PROGRESS;
          }
          if (s.wrapQuote) {
            s.wrapQuote = {
              ...s.wrapQuote,
              hash: txHash,
            };
          }
        });
        const transaction = await waitForTransaction({
          hash: txHash,
        });
        if (transaction?.status === "success") {
          GlobalStore.update(s => {
            if (isWrapPage) {
              s.wrapStatus = WrapStatus.COMPLETED;
            } else {
              s.orderStatus = OrderStatus.COMPLETED;
            }
          });
        }
      }
    }
  }, [
    state.wrapQuote,
    state.network,
    data,
    walletClient,
    tokenMetadata,
    wrappedToken,
    getTokenDecimals,
    isWrapPage,
  ]);

  const handleOnClick = useCallback(() => {
    if (wrapQuote) {
      if (
        errorState === WrapActionButtonState.Wrap ||
        errorState === WrapActionButtonState.Unwrap
      ) {
        GlobalStore.update(s => {
          s.wrapStatus = WrapStatus.REVIEW;
        });
      } else if (errorState === WrapActionButtonState.ConfirmOrder) {
        void confirmWrapping();
      } else if (errorState === WrapActionButtonState.WalletNotConnected) {
        return "";
      } else if (errorState === WrapActionButtonState.ViewTransaction && wrapQuote.hash) {
        window.open(`${BLOCK_EXPLORER_URL_BY_NETWORK[state.network.id]}tx/${wrapQuote.hash}`);
      }
    }
  }, [errorState, wrapQuote, state.network, confirmWrapping]);

  const getDisabledAtr = useCallback(() => {
    if (errorState) {
      return BUTTON_DISABLED_MAP[errorState];
    }
    return BUTTON_DISABLED_MAP.Wrap;
  }, [errorState]);

  return (
    <>
      <ConnectButton.Custom>
        {({ account, chain, openConnectModal, mounted }) => {
          const ready = mounted;
          const connected = ready && account && chain;
          return (
            <button
              type="button"
              className={`btn-lg btn-block text-dark-bebop button-font-size ` + getClassName()}
              onClick={!connected ? openConnectModal : handleOnClick}
              disabled={getDisabledAtr()}
              data-testid={`${state.wrapStatus}-wrap-action-btn`}
              data-testid-tx-hash={txHash ?? ""}
              {...(!ready && {
                "aria-hidden": true,
                style: {
                  opacity: 0,
                  pointerEvents: "none",
                  userSelect: "none",
                },
              })}
            >
              {state.wrapStatus === WrapStatus.CONFIRMING ? (
                <Spinner />
              ) : (
                <TextContent>
                  {getButtonIcon()}
                  <span data-testid="action-btn-text">{buttonText}</span>
                </TextContent>
              )}
            </button>
          );
        }}
      </ConnectButton.Custom>
      <ConfirmTradeModal />
    </>
  );
};

// Sub-components

const TextContent = styled.span`
  display: flex;
  gap: 7px;
  justify-content: center;
`;
